import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import { useDecodedToken } from '../utils/useDecodedToken';

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useDecodedToken();
  const [status, setStatus] = useState<'checking' | 'allowed' | 'redirect'>('checking');

  useEffect(() => {
    const checkAuth = () => {
      if (!isAuthenticated()) {
        setStatus('redirect');
        return;
      }

      // Check if user has ADMINISTRATOR role
      // Roles can be in realm_access.roles or resource_access.{client-id}.roles
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const { jwtDecode } = require('jwt-decode');
          const decoded: any = jwtDecode(token);
          
          // Extract roles from token
          const realmRoles = decoded.realm_access?.roles || [];
          const resourceRoles: string[] = [];
          
          if (decoded.resource_access) {
            Object.values(decoded.resource_access).forEach((client: any) => {
              if (client.roles) {
                resourceRoles.push(...client.roles);
              }
            });
          }
          
          const allRoles = [...realmRoles, ...resourceRoles];
          
          // Define all admin roles that should have access to admin dashboard
          const adminRoles = [
            'ADMINISTRATOR', 'ROLE_ADMINISTRATOR',
            'ACCOUNTING_ASSISTANT', 'ROLE_ACCOUNTING_ASSISTANT',
            'ASSISTANT_ACCOUNTANT', 'ROLE_ASSISTANT_ACCOUNTANT',
            'CEO', 'ROLE_CEO',
            'FINANCE_MANAGER', 'ROLE_FINANCE_MANAGER',
            'OPERATIONS_MANAGER', 'ROLE_OPERATIONS_MANAGER',
            'OPERATIONS', 'ROLE_OPERATIONS',
            'FINANCE_DIRECTOR', 'ROLE_FINANCE_DIRECTOR',
            'OPERATIONS_DIRECTOR', 'ROLE_OPERATIONS_DIRECTOR',
            'RISK', 'ROLE_RISK',
            'AUDITOR', 'ROLE_AUDITOR',
            'AUDIT', 'ROLE_AUDIT'
          ];
          
          const hasAdminRole = allRoles.some((role: string) => 
            adminRoles.includes(role.toUpperCase())
          );
          
          if (hasAdminRole) {
            setStatus('allowed');
          } else {
            setStatus('redirect');
          }
        } catch (error) {
          console.error('Error checking admin role:', error);
          // Don't redirect on decode error - might be a temporary issue
          // Only redirect if token is completely missing
          if (!token) {
            setStatus('redirect');
          } else {
            // If we have a token but can't decode it, still allow access
            // The backend will handle authorization
            setStatus('allowed');
          }
        }
      } else {
        setStatus('redirect');
      }
    };

    checkAuth();
    
    // Listen for token changes to re-check auth status
    const handleStorageChange = () => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (for same-tab updates)
    window.addEventListener('userInfoChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userInfoChanged', handleStorageChange);
    };
  }, []);

  if (status === 'checking') {
    return <div>Checking permissions...</div>;
  }

  if (status === 'redirect') {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;

